import { Dialog, Transition, TransitionChild } from "@headlessui/react";
import clsx from "clsx";
import { motion } from "framer-motion";
import React, { FC, Fragment, PropsWithChildren } from "react";

import { Icon } from "../icon";

export interface ModalProps {
  isOpen?: boolean;
  onClose: () => void;
  title?: string;
  fullScreen?: boolean;
}

export const Modal: FC<PropsWithChildren<ModalProps>> = ({
  isOpen,
  onClose,
  title,
  children,
  fullScreen,
}) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        static
        open={isOpen}
        as={motion.div}
        transition={{ easing: "easeOut", duration: 1 }}
        className="relative z-[120]"
        onClose={onClose}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-primary-dark/80" />
        </TransitionChild>
        <div
          className={clsx(
            "fixed inset-0 flex overflow-y-auto",
            fullScreen && "overflow-x-hidden no-scrollbar"
          )}
        >
          <div
            className={clsx(
              "min-h-full mx-auto pt-[10%] overflow-visible relative text-center",
              fullScreen ? "w-screen lg:w-fit" : "p-8"
            )}
          >
            <TransitionChild
              as="div"
              className="relative"
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-10"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-10"
            >
              <Dialog.Panel className="w-full rounded-4xl bg-neutral p-4 shadow-xl transition-all">
                {title && (
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-primary mb-2">
                    {title}
                  </Dialog.Title>
                )}
                {children}
              </Dialog.Panel>
              <button
                type="button"
                className={clsx(
                  "absolute text-white",
                  fullScreen ? "-top-8 -right-0" : "-top-4 -right-8"
                )}
                onClick={onClose}
              >
                <Icon icon="close" size={32} />
              </button>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
